import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, Users, ExternalLink } from "lucide-react";

const PROGRAMMES = [
  {
    name: "NHS Graduate Management Training Scheme (GMTS)",
    desc: "Highly competitive NHS leadership development scheme. Pharmacy graduates can apply to the finance, health analysis, or general management streams to develop strategic leadership alongside clinical career.",
    url: "https://www.nhsgraduatescheme.co.uk/",
    badge: "Competitive",
  },
  {
    name: "NHS Leadership Academy — Edward Jenner Programme",
    desc: "Free online leadership development programme open to all NHS staff. Covers core leadership behaviours, NHS values, and team leadership. Good starting point for pharmacists at Band 6/7.",
    url: "https://www.leadershipacademy.nhs.uk/programmes/the-edward-jenner-programme/",
    badge: "All Bands",
  },
  {
    name: "NHS Leadership Academy — Mary Seacole Programme",
    desc: "Designed for those taking on their first leadership role. Focuses on developing leadership identity and skills. Relevant for Band 7/8a pharmacists moving into management responsibilities.",
    url: "https://www.leadershipacademy.nhs.uk/programmes/mary-seacole-programme/",
    badge: "Band 7–8a",
  },
  {
    name: "NHS Leadership Academy — Nye Bevan Programme",
    desc: "Senior leadership programme for those aspiring to executive or very senior manager roles. Relevant for Consultant Pharmacists and those targeting Chief Pharmacist positions.",
    url: "https://www.leadershipacademy.nhs.uk/programmes/nye-bevan-programme/",
    badge: "Band 8+",
  },
  {
    name: "RPS Leadership Development",
    desc: "The RPS provides pharmacy-specific leadership development through events, mentoring, and the RPS Board and committee infrastructure. Active involvement in RPS networks builds leadership profile.",
    url: "https://www.rpharms.com/development/leadership",
    badge: "RPS Members",
  },
  {
    name: "Q Community (NHS England / Health Foundation)",
    desc: "A connected community of people with experience in quality improvement across health and care. Joining Q develops QI skills and networks relevant to pharmacy service leadership.",
    url: "https://q.health.org.uk/",
    badge: "QI Focus",
  },
];

export default function PharmacyLeadership() {
  return (
    <PharmacistLayout title="Pharmacy Leadership">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-teal-800 to-teal-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Users className="h-7 w-7 text-teal-300" />
            <div>
              <h2 className="text-xl font-bold">Pharmacy Leadership</h2>
              <p className="text-teal-300 text-sm">Chief Pharmacist pathway, NHS leadership programmes, and strategic roles</p>
            </div>
          </div>
          <p className="text-teal-100 text-sm leading-relaxed">
            Leadership in pharmacy extends from managing a small team to directing trust-wide medicines strategy as Chief Pharmacist. A structured approach to leadership development — combining clinical expertise with management skills — opens the most senior strategic roles.
          </p>
        </div>

        {/* Chief Pharmacist pathway */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Chief Pharmacist Pathway</h3>
          <p className="text-sm text-slate-600 mb-4 leading-relaxed">
            Chief Pharmacist is typically a Band 9 or Very Senior Manager (VSM) role, accountable to the Chief Medical Officer or Chief Executive. It encompasses medicines governance, quality, safety, and the professional leadership of all pharmacy staff.
          </p>
          <div className="space-y-3">
            {[
              { phase: "Band 8b–8c", title: "Deputy Chief Pharmacist / Lead Pharmacist (Operations or Clinical)", desc: "Manage a significant service area. Contribute to trust-level governance. Develop budget and business case skills." },
              { phase: "Band 8d", title: "Deputy Chief Pharmacist / Service Director", desc: "Broad operational or clinical leadership across pharmacy departments. Line management of multiple team leads." },
              { phase: "Band 9 / VSM", title: "Chief Pharmacist", desc: "Responsible for the pharmacy service as a whole. Member of the Medicines Management Committee. Reports to executive board. Responsible for CQC pharmacy readiness, NHSE contractual compliance, and strategic medicines policy." },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                <span className="shrink-0 bg-teal-100 text-teal-800 text-xs font-bold px-2.5 py-1.5 rounded-lg mt-0.5">{step.phase}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership skills */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">Core Leadership Competencies to Develop</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: "Financial Management", desc: "Understanding NHS budgets, business cases, QIPP, and high-cost drug spend management" },
              { title: "HR & People Leadership", desc: "Recruitment, appraisal, capability management, and building high-performing pharmacy teams" },
              { title: "Strategic Thinking", desc: "Translating NHS strategy (e.g. NHSE Long Term Plan) into local pharmacy service development" },
              { title: "Governance & Safety", desc: "Medicines safety culture, CAS alerts, SOP governance, and regulatory compliance (GPhC, CQC)" },
              { title: "Stakeholder Management", desc: "Engaging ICBs, NHSE, Medical Directors, Nursing Directors, and external pharmaceutical industry" },
              { title: "Quality Improvement", desc: "Leading trust-wide QI programmes, audit cycles, and service redesign using QI methodologies" },
            ].map((skill, i) => (
              <div key={i} className="p-3.5 bg-teal-50 border border-teal-200 rounded-xl">
                <p className="text-sm font-semibold text-teal-900 mb-1">{skill.title}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{skill.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Programmes */}
        <div>
          <h3 className="font-bold text-slate-900 mb-3">Leadership Development Programmes</h3>
          <div className="space-y-3">
            {PROGRAMMES.map((prog) => (
              <div key={prog.name} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-slate-900">{prog.name}</p>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium shrink-0">{prog.badge}</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{prog.desc}</p>
                  </div>
                </div>
                <a
                  href={prog.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2.5 text-xs text-teal-600 hover:text-teal-800 font-medium"
                >
                  <ExternalLink className="h-3 w-3" />
                  Learn more
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PharmacistLayout>
  );
}
